import { TransactionMutationBuilder } from '@seen/core/bundle-builder'
import { dataOperationCancel } from '@seen/core/codeSystems'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import * as appointments from '@seen/core/models/appointments/index'
import {
  ExtendedProvenance,
  ProvenanceParticipantType,
} from '@seen/core/models/provenance'
import type { ButtonProps } from '@seen/ui'
import { Label, TypographyPUI } from '@seen/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type JSX, useState } from 'react'
import { z } from 'zod'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogButton } from '#src/components/core/dialog/DialogButton.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormLabel } from '#src/components/core/form/FormLabel.tsx'
import { SubmitButton } from '#src/components/core/form/SubmitButton.tsx'
import { RadioGroup } from '#src/components/core/radio-group/RadioGroup.tsx'
import { RadioGroupItem } from '#src/components/core/radio-group/RadioGroupItem.tsx'
import { Textarea } from '#src/components/core/Textarea.tsx'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

interface CancelAppointmentButtonProps extends ButtonProps {
  appointment: appointments.ExtendedAppointment
  label?: React.ReactNode
}

export function CancelAppointmentButton({
  appointment,
  label = 'Cancel Appointment',
  ...buttonProps
}: CancelAppointmentButtonProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const rumStableActionNames = useFeatureFlag(featureFlags.rumStableActionNames)
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogButton
        variant="outline"
        color="red"
        size="sm"
        data-dd-action-name={
          rumStableActionNames ? 'appointments.appointment.cancel' : undefined
        }
        {...buttonProps}
      >
        {label}
      </DialogButton>
      <DialogDisplay>
        <DialogHeader asChild>
          <DialogTitle>
            {!appointment.recurrenceInstanceLink && 'Cancel appointment'}
            {!!appointment.recurrenceInstanceLink &&
              'Cancel recurring appointment'}
          </DialogTitle>
        </DialogHeader>
        <DialogContent>
          <CancelAppointmentForm
            appointment={appointment}
            onClose={() => setOpen(false)}
          />
        </DialogContent>
      </DialogDisplay>
    </Dialog>
  )
}

function CancelAppointmentForm({
  appointment,
  onClose,
}: {
  appointment: appointments.ExtendedAppointment
  onClose: () => void
}): JSX.Element {
  const practitioner = useCurrentPractitioner()

  const form = useForm({
    schema: z.object({
      includeFutureAppointments: z.boolean(),
      reason: z.string().min(1, 'Reason is required'),
    }),
    defaultValues: {
      includeFutureAppointments: false,
      reason: '',
    },
  })

  const medplum = useMedplum()
  const queryClient = useQueryClient()

  const cancelMutationQuery = useMutation<
    unknown,
    unknown,
    {
      appointment: appointments.ExtendedAppointment
      reason: string
      includeFutureAppointments: boolean
    }
  >({
    mutationFn: async ({ appointment, reason, includeFutureAppointments }) => {
      invariant(appointment.id, 'Appointment ID is required')

      const appointmentsToCancel = [appointment]

      if (includeFutureAppointments) {
        const recurrenceLink = appointment.recurrenceInstanceLink
        invariant(recurrenceLink?.code, 'Recurrence link is required')
        const futureAppointmentsInGroup = await medplum.searchResourceClass(
          appointments.ExtendedAppointment,
          {
            date: `gt${appointment.start}`,
            status: 'booked',
            _tag: recurrenceLink.code,
            _count: 1000,
          }
        )
        appointmentsToCancel.push(...futureAppointmentsInGroup)
      }

      const transaction = new TransactionMutationBuilder()

      for (const appointment of appointmentsToCancel) {
        transaction.patch(appointment, (appt) => {
          appt.status = 'cancelled'
          appt.comment = reason
        })
        transaction.create(
          new ExtendedProvenance({
            target: appointment.reference,
            who: practitioner.reference,
            type: ProvenanceParticipantType.AUTHOR,
            activity: dataOperationCancel,
          })
        )
      }

      return medplum.executeBatch(transaction.bundle)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: cacheKeys.allAppointments,
      })
      onClose()
    },
  })

  const handleSubmit = form.handleSubmit((values) => {
    cancelMutationQuery.mutate({
      appointment,
      reason: values.reason,
      includeFutureAppointments: values.includeFutureAppointments,
    })
  })

  return (
    <Form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {!!appointment.recurrenceInstanceLink && (
        <FormField
          control={form.control}
          name="includeFutureAppointments"
          render={({ field: { value, onChange, ...rest } }) => (
            <RadioGroup
              value={
                value ? 'this-and-following-appointments' : 'this-appointment'
              }
              onChange={(v) =>
                onChange(v === 'this-and-following-appointments')
              }
              className="flex flex-col gap-4"
              {...rest}
            >
              <Label className="flex items-center gap-2">
                <RadioGroupItem value="this-appointment" />
                <TypographyPUI>This appointment</TypographyPUI>
              </Label>
              <Label className="flex items-center gap-2">
                <RadioGroupItem value="this-and-following-appointments" />
                <TypographyPUI>This and following appointments</TypographyPUI>
              </Label>
            </RadioGroup>
          )}
        />
      )}
      <FormField
        control={form.control}
        name="reason"
        render={({ field }) => (
          <div>
            <FormLabel>Reason</FormLabel>
            <Textarea {...field} />
          </div>
        )}
      />
      <SubmitButton
        submitting={cancelMutationQuery.isPending}
        className="self-end"
      >
        Submit
      </SubmitButton>
    </Form>
  )
}
